import { z } from "../wire/zod";

export const createLinkTypeResponseSchema = z.object({ linkType: z.record(z.string(), z.any()) });
export const getAdoMappingResponseSchema = z.object({
  mapping: z.array(
    z.object({
      piIterationId: z.number().int().describe("Index of the iteration in the PI planning session"),
      teams: z.array(
        z
          .object({
            teamId: z.number().int(),
            sources: z
              .array(
                z
                  .object({
                    id: z
                      .number()
                      .int()
                      .describe("Id of the ALM source (project) the iteration belongs to"),
                    iterationPath: z
                      .union([
                        z
                          .string()
                          .describe("Backslash-separated ADO iteration path, null while unmapped"),
                        z
                          .null()
                          .describe("Backslash-separated ADO iteration path, null while unmapped"),
                      ])
                      .describe("Backslash-separated ADO iteration path, null while unmapped")
                      .optional(),
                  })
                  .catchall(z.any()),
              )
              .default([]),
            syncTimestamp: z
              .number()
              .int()
              .describe("Unix time of the team's last sync, set by the server (responses only)")
              .optional(),
          })
          .catchall(z.any()),
      ),
    }),
  ),
});
export const getAlmItemTypeForStickyResponseSchema = z.object({
  almItemType: z.object({
    id: z.string(),
    statuses: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        category: z.enum(["todo", "in-progress", "done", "undefined"]),
        order: z.number().int().optional(),
      }),
    ),
    transitions: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        type: z
          .enum(["initial", "global", "normal"])
          .describe(
            "initial: used when a sticky is created; global: allowed from every status; normal: allowed from from_statuses",
          ),
        fromStatuses: z.array(z.string()),
        toStatus: z.string(),
      }),
    ),
    dynamic: z.boolean().describe("Statuses and transitions must be fetched per issue"),
  }),
});
export const getAlmItemTypeResponseSchema = z.object({
  almItemType: z
    .union([
      z
        .object({
          transitions: z.record(
            z.string(),
            z.object({
              id: z.string(),
              name: z.string(),
              type: z
                .enum(["initial", "global", "normal"])
                .describe(
                  "initial: used when a sticky is created; global: allowed from every status; normal: allowed from from_statuses",
                ),
              fromStatuses: z.array(z.string()),
              toStatus: z.string(),
            }),
          ),
          almItemTypesByBoardKey: z.record(
            z.string(),
            z.record(
              z.string(),
              z.object({
                id: z.string(),
                statuses: z.array(
                  z.object({
                    id: z.string(),
                    name: z.string(),
                    category: z.enum(["todo", "in-progress", "done", "undefined"]),
                    order: z.number().int().optional(),
                  }),
                ),
                transitions: z
                  .array(z.string())
                  .describe("Keys into NormalizedAlmItemTypeMap.transitions"),
                dynamic: z.boolean(),
              }),
            ),
          ),
        })
        .describe(
          "AlmItemTypeMap with the transitions deduplicated: each item type lists transition hashes that key into transitions",
        ),
      z
        .record(
          z.string(),
          z.record(
            z.string(),
            z.object({
              id: z.string(),
              statuses: z.array(
                z.object({
                  id: z.string(),
                  name: z.string(),
                  category: z.enum(["todo", "in-progress", "done", "undefined"]),
                  order: z.number().int().optional(),
                }),
              ),
              transitions: z.array(
                z.object({
                  id: z.string(),
                  name: z.string(),
                  type: z
                    .enum(["initial", "global", "normal"])
                    .describe(
                      "initial: used when a sticky is created; global: allowed from every status; normal: allowed from from_statuses",
                    ),
                  fromStatuses: z.array(z.string()),
                  toStatus: z.string(),
                }),
              ),
              dynamic: z.boolean().describe("Statuses and transitions must be fetched per issue"),
            }),
          ),
        )
        .describe(
          'Item types by board key (team id or ART id, "" = default), then by source id ("" = default)',
        ),
    ])
    .describe(
      "Jira, ADO and Rally answer the normalized map; the cross-ALM fallback (almsync common) answers the plain map",
    ),
});
export const getAlmTypesResponseSchema = z.object({
  almTypes: z.array(z.record(z.string(), z.any())),
});
export const getAlmUserResponseSchema = z
  .object({
    almId: z
      .union([
        z.string().describe("Null while the caller has no ALM user mapped"),
        z.null().describe("Null while the caller has no ALM user mapped"),
      ])
      .describe("Null while the caller has no ALM user mapped"),
    pipId: z.union([z.string(), z.null()]),
    almName: z.string().describe("Empty when no ALM user is mapped or it cannot be resolved"),
  })
  .describe("The ALM user mapped to the calling PI planning user");
export const getArtTeamFieldMappingResponseSchema = z.object({
  artTeamFieldMapping: z.record(z.string(), z.any()).optional(),
});
export const getDevLinksResponseSchema = z.object({
  devLinks: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      url: z.string(),
      devStatus: z.enum(["OPEN", "MERGED", "DECLINED"]),
      source: z.string().describe("Source provider name (e.g. GitLab, GitHub)").optional(),
      branch: z.string().optional(),
      repositoryName: z.string().describe("Name of the repository the MR/PR belongs to").optional(),
      repositoryUrl: z.string().describe("URL of the repository the MR/PR belongs to").optional(),
    }),
  ),
});
export const getIterationStatusResponseSchema = z.object({
  statuses: z
    .array(
      z.object({
        status: z.enum(["stopped", "starting", "running", "error", "synced", "syncing"]).optional(),
        piIterationId: z.number().int().optional(),
      }),
    )
    .optional(),
});
export const getJiraBoardsResponseSchema = z.object({
  boards: z.array(z.record(z.string(), z.any())),
});
export const getJiraKanbanBoardResponseSchema = z.object({
  boardId: z.union([z.number().int(), z.null()]).optional(),
});
export const getJiraKanbanConfigResponseSchema = z.object({
  config: z
    .union([
      z.object({
        name: z.string(),
        columns: z.array(z.object({ name: z.string(), statuses: z.array(z.string()) })),
      }),
      z.null(),
    ])
    .optional(),
});
export const getJiraMappingResponseSchema = z.object({
  mapping: z.array(
    z.object({
      piIterationId: z.number().int().describe("Index of the iteration in the PI planning session"),
      teams: z.array(
        z
          .object({
            teamId: z.number().int(),
            sources: z
              .array(
                z
                  .object({
                    id: z
                      .number()
                      .int()
                      .describe("Id of the ALM source (board) the sprint belongs to"),
                    sprint: z
                      .union([
                        z.number().int().describe("Jira sprint id, null while unmapped"),
                        z.null().describe("Jira sprint id, null while unmapped"),
                      ])
                      .describe("Jira sprint id, null while unmapped")
                      .optional(),
                  })
                  .catchall(z.any()),
              )
              .default([]),
            syncTimestamp: z
              .number()
              .int()
              .describe("Unix time of the team's last sync, set by the server (responses only)")
              .optional(),
          })
          .catchall(z.any()),
      ),
    }),
  ),
});
export const getKanbanConfigsResponseSchema = z.object({
  configs: z
    .record(
      z.string(),
      z.array(
        z
          .object({
            name: z.string(),
            columns: z.array(z.object({ name: z.string(), statuses: z.array(z.string()) })),
          })
          .describe("A Kanban board configuration of the ALM tool, as shown on team boards"),
      ),
    )
    .describe("Kanban configs per team id (JSON object keys, so the ids are strings on the wire)"),
});
export const getProjectSourcesResponseSchema = z.object({
  projectSources: z.array(z.record(z.string(), z.any())),
});
export const getRallyMappingResponseSchema = z.object({
  mapping: z.array(
    z.object({
      piIterationId: z.number().int().describe("Index of the iteration in the PI planning session"),
      teams: z.array(
        z
          .object({
            teamId: z.number().int(),
            sources: z
              .array(
                z
                  .object({
                    id: z
                      .number()
                      .int()
                      .describe("Id of the ALM source (project) the iteration belongs to"),
                    acIterationId: z
                      .union([
                        z.number().int().describe("Rally iteration object-id, null while unmapped"),
                        z.null().describe("Rally iteration object-id, null while unmapped"),
                      ])
                      .describe("Rally iteration object-id, null while unmapped")
                      .optional(),
                  })
                  .catchall(z.any()),
              )
              .default([]),
            syncTimestamp: z
              .number()
              .int()
              .describe("Unix time of the team's last sync, set by the server (responses only)")
              .optional(),
          })
          .catchall(z.any()),
      ),
    }),
  ),
});
export const getTeamFieldMappingsResponseSchema = z.object({
  teamFieldMappings: z.array(z.record(z.string(), z.any())).optional(),
});
export const loadAlmItemTypesResponseSchema = z.object({
  almItemTypes: z.array(z.record(z.string(), z.any())).optional(),
});
export const noDataSchema = z.record(z.string(), z.never());
export const searchAlmUsersResponseSchema = z.object({
  users: z
    .array(
      z
        .object({
          almId: z.string(),
          pipId: z
            .union([
              z.string().describe("Null while no PI planning user is mapped to this ALM user"),
              z.null().describe("Null while no PI planning user is mapped to this ALM user"),
            ])
            .describe("Null while no PI planning user is mapped to this ALM user"),
          almName: z.string(),
        })
        .describe("An ALM user matching a search, with the PI planning user mapped to it"),
    )
    .optional(),
});
export const startIterationSyncResponseSchema = z.object({
  status: z
    .enum(["syncing", "synced"])
    .describe(
      "Outcome of asking for a sync: syncing = a sync of this target is already running, synced = the sync ran to completion",
    ),
});
export const startProgramBacklogSyncResponseSchema = z.object({
  status: z
    .enum(["syncing", "synced"])
    .describe(
      "Outcome of asking for a sync: syncing = a sync of this target is already running, synced = the sync ran to completion",
    ),
});
export const startSessionResponseSchema = z.object({ started: z.boolean() });
